import { useCallback, useEffect, useState } from 'react'
import { FileX, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchTakedowns,
  createTakedown,
  updateStatus,
  deleteTakedown,
  CONTENT_TYPES,
  SOURCES,
  sourceLabel,
  statusMeta,
  TD_STR as S,
  type Takedown,
} from './takedownApi'

export function TakedownScreen() {
  const { toast } = useToast()
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<Takedown[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [contentType, setContentType] = useState('message')
  const [contentRef, setContentRef] = useState('')
  const [source, setSource] = useState('dmca')
  const [complainant, setComplainant] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchTakedowns(status, ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, status])

  const create = async () => {
    if (!contentRef.trim() && !reason.trim()) {
      toast(S.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await createTakedown({ content_type: contentType, content_ref: contentRef.trim(), reason: reason.trim(), source, complainant: complainant.trim() })
      setContentRef('')
      setComplainant('')
      setReason('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const setStat = async (id: number, st: string) => {
    let resolution = ''
    if (st === 'removed' || st === 'rejected') resolution = window.prompt(S.reason) ?? ''
    try {
      await updateStatus(id, st, resolution)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const del = async (id: number) => {
    try {
      await deleteTakedown(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.title} screenId="A19.07" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Create */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <select value={contentType} onChange={(e) => setContentType(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary">
              {CONTENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <input
              value={contentRef}
              onChange={(e) => setContentRef(e.target.value)}
              placeholder={S.contentRef}
              className="w-40 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <select value={source} onChange={(e) => setSource(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary">
              {SOURCES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
            <input
              value={complainant}
              onChange={(e) => setComplainant(e.target.value)}
              placeholder={S.complainant}
              className="w-32 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={S.reason}
              className="min-w-[120px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <button type="button" onClick={create} disabled={busy} className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
              <FileX size={14} />
              {S.add}
            </button>
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary">
            <option value="">{S.all}</option>
            <option value="open">open</option>
            <option value="reviewing">reviewing</option>
            <option value="removed">removed</option>
            <option value="rejected">rejected</option>
          </select>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {rows.map((r) => {
                const st = statusMeta(r.status)
                return (
                  <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-primary">{sourceLabel(r.source)}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs ${st.pill}`}>{st.label}</span>
                          <span className="text-xs text-tertiary">{r.content_type}</span>
                        </div>
                        {r.content_ref && <p className="mt-1 font-mono text-xs text-secondary">{S.content}: {r.content_ref}</p>}
                        {r.reason && <p className="text-xs text-tertiary">{r.reason}</p>}
                        {r.complainant && <p className="text-xs text-tertiary">— {r.complainant}</p>}
                        {r.resolution && <p className="text-xs text-tertiary">→ {r.resolution}</p>}
                      </div>
                      <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                        {r.status !== 'removed' && r.status !== 'rejected' && (
                          <>
                            {r.status === 'open' && (
                              <button type="button" onClick={() => setStat(r.id, 'reviewing')} className="rounded-md border border-border-default px-2 py-1 text-xs text-secondary hover:text-primary">
                                {S.reviewing}
                              </button>
                            )}
                            <button type="button" onClick={() => setStat(r.id, 'removed')} className="rounded-md bg-success px-2 py-1 text-xs text-white">
                              {S.remove}
                            </button>
                            <button type="button" onClick={() => setStat(r.id, 'rejected')} className="rounded-md border border-border-default px-2 py-1 text-xs text-secondary hover:text-primary">
                              {S.reject}
                            </button>
                          </>
                        )}
                        <button type="button" onClick={() => del(r.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
